// Services
import breedingManagementService from "@/app/services/main/breeding-management/breeding-management-service.js";

// Mixins
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins";
import { dateMixins } from "@/app/mixins/utils/date-mixins";

// Constants
import { STATUS_CODES, SUCCESS_MSG } from "@/app/constants/status-constants";
import { BREEDER_ROUTES } from "@/app/constants/breeder-routes-constants";

// Components
const AddMatingContainer = () => import(/*webpackChunkName: "add-mating-container" */ "@/app/views/main-view/breeding-management/mating/containers/add-mating-container/add-mating-container.vue");

const GestationPeriodContainer = () =>
    import(/*webpackChunkName: "gestation-period-container" */ "@/app/views/main-view/breeding-management/gestation-period/containers/gestation-period-container/gestation-period-container.vue");

export default {
    name: "AddMatingOvulationGestationWhelpingContainer",
    components: {
        AddMatingContainer,
        GestationPeriodContainer,
    },
    directive: {},
    filters: {},
    mixins: [toastMessageMixins, dateMixins],
    props: {},
    data() {
        return {
            isAPILoading: false,
            isAPISubmitLoading: false,

            selectedDam: null,
            selectedSire: null,
            matingNotes: null,
            matingId: null,

            firstDayOfCycle: null,
            matingDate: null,
            whelpingDate: null,
            typeOfBirth: null,

            isAddingSuccess: false,
            initialValues: {},

            prevRoute: "Mating List",
            prevRouteTo: BREEDER_ROUTES.MATING_LIST,

            isRedirectToLitter: false,
        };
    },
    computed: {},
    watch: {},
    created() {
        if (this.$route.query) {
            if (this.$route.query.from === "ovulation-mating") {
                this.prevRoute = "Ovulation / Mating";
                this.prevRouteTo = `${BREEDER_ROUTES.EDIT_MATING}?dam=${this.$route.query.dam}&from=add-mating`;
            } else if (this.$route.query.from === "mating-list") {
                this.prevRoute = "Mating List";
                this.prevRouteTo = BREEDER_ROUTES.MATING_LIST;
            } else if (this.$route.query.from === "dam-details") {
                this.prevRoute = "Dam/Sire Details";
                this.prevRouteTo = `${BREEDER_ROUTES.EDIT_DAM_SIRE}?id=${this.$route.query.dam}&from=add-mating`;
            }

            if (this.$route.query.dam) {
                this.selectedDam = {
                    id: parseFloat(this.$route.query.dam),
                };
            }
        }
    },
    methods: {
        async onSubmitDetails() {
            try {
                if (!this.selectedDam && !this.selectedSire) {
                    await this.$swal({
                        text: "Please select either Dam or Sire.",
                        imageHeight: "30px",
                        imageUrl: `${require("@/assets/images/_shared/sad-dog.png")}`,
                        imageAlt: "Sad Dog",
                        confirmButtonText: "Got it",
                        showCloseButton: true,
                    });

                    return null;
                }

                if (!this.selectedDam && this.selectedSire) {
                    await this.$swal({
                        text: "Please select a Dam as well.",
                        imageHeight: "30px",
                        imageUrl: `${require("@/assets/images/_shared/sad-dog.png")}`,
                        imageAlt: "Sad Dog",
                        confirmButtonText: "Got it",
                        showCloseButton: true,
                    });

                    return null;
                }

                this.isAPISubmitLoading = true;
                if (this.firstDayOfCycle || this.matingDate || this.whelpingDate) {
                    // call API which saves both mating and cycle dates
                    await this.saveMatingAndOvulationGestationWhelping();
                } else {
                    // call API which ONLY save mating details, as neither firstDayOfCycle nor matingDate is provided
                    await this.saveMatingDetails("ADD");
                }
                
                if(this.isRedirectToLitter) {
                    this.$router.push(`${BREEDER_ROUTES.ADD_LITTER}?id=${this.matingId}&from=mating-details`);
                }
            } catch (err) {
                this.showGenericErrorToast();
            } finally {
                this.isAPISubmitLoading = false;
            }
        },
        async saveMatingAndOvulationGestationWhelping() {
            const dateFormat = "yyyy-MM-dd";

            const matingDetails = {
                damId: this.selectedDam ? this.selectedDam.id : null,
                sireId: this.selectedSire ? this.selectedSire.id : null,
                note: this.matingNotes,
            };

            const cycleDetails = {
                damId: this.selectedDam.id,
                ovulationStartDate: this.firstDayOfCycle ? this.formatDateWithoutConversion(this.firstDayOfCycle, dateFormat) : null,
                matingDate: this.matingDate ? this.formatDateWithoutConversion(this.matingDate, dateFormat) : null,
                whelpingDate: this.whelpingDate ? this.formatDateWithoutConversion(this.whelpingDate, dateFormat) : null,
                typeOfBirth: this.typeOfBirth || null,
            };

            const detailsToSave = {
                matingDetails,
                cycleDetails,
            };

            const response = await breedingManagementService.saveMatingAndOvulationGestationWhelping(detailsToSave);

            const { SUCCESS_ADD_MATING_AND_CYCLE, ERROR_ADD_MATING_AND_CYCLE } = this.toastMessages.matingAndCycle();

            if (response.status === STATUS_CODES.SUCCESS_POST) {
                this.showCustomSuccessToast(SUCCESS_MSG.SUCCESS_TITLE, SUCCESS_ADD_MATING_AND_CYCLE);

                this.isAddingSuccess = true;
                this.$router.push(BREEDER_ROUTES.MATING_LIST);
                this.matingId = response.data.matingId
            } else {
                this.showAPIErrorToast(ERROR_ADD_MATING_AND_CYCLE, response);
            }
        },
        async saveMatingDetails(actionType) {
            try {
                const matingDetails = {
                    damId: this.selectedDam ? this.selectedDam.id : null,
                    sireId: this.selectedSire ? this.selectedSire.id : null,
                    note: this.matingNotes,
                };

                let response = null;

                if (actionType === "ADD") {
                    response = await breedingManagementService.addDamAndSireMatingDetails(matingDetails);

                    const { SUCCESS_ADD_MATING, ERROR_ADD_MATING } = this.toastMessages.mating();

                    if (response.status === STATUS_CODES.SUCCESS_POST) {
                        this.showCustomSuccessToast(SUCCESS_MSG.SUCCESS_TITLE, SUCCESS_ADD_MATING);

                        this.matingId = response.data;

                        this.isAddingSuccess = true;
                        this.$router.push(BREEDER_ROUTES.MATING_LIST);
                    } else {
                        this.showAPIErrorToast(ERROR_ADD_MATING, response);
                    }
                } else {
                    response = await breedingManagementService.updateDamAndSireMatingDetails(this.matingId, matingDetails);

                    const { SUCCESS_UPDATE_MATING, ERROR_UPDATE_MATING } = this.toastMessages.mating();

                    if (response.status === STATUS_CODES.SUCCESS_NO_CONTENT) {
                        this.showCustomSuccessToast(SUCCESS_MSG.SUCCESS_TITLE, SUCCESS_UPDATE_MATING);
                        this.getDamAndSireMatingDetails(this.matingId);
                        this.isAddingSuccess = true;
                    } else {
                        this.showAPIErrorToast(ERROR_UPDATE_MATING, response);
                    }
                }

                return response.data;
            } catch (err) {
                throw err;
            }
        },
        async saveOvulationGestationWhelpingDates() {
            try {
                const { SUCCESS_ADD_OVULATION_GESTATION, ERROR_ADD_OVULATION_GESTATION } = this.toastMessages.ovulationGestation();
                let isSavingSuccess = false;

                // format dates to properly save them (not index 0)
                const detailsToSave = {
                    damId: this.selectedDam.id,
                    ovulationStartDate: this.firstDayOfCycle ? this.formatDateWithoutConversion(this.firstDayOfCycle, "yyyy-MM-dd") : null,
                    matingDate: this.matingDate ? this.formatDateWithoutConversion(this.matingDate, "yyyy-MM-dd") : null,
                };

                const response = await breedingManagementService.saveOvulationAndGestation(detailsToSave);

                if (response.status === STATUS_CODES.SUCCESS_POST) {
                    this.showCustomSuccessToast(SUCCESS_MSG.SUCCESS_TITLE, SUCCESS_ADD_OVULATION_GESTATION);
                    isSavingSuccess = true;
                } else {
                    this.showAPIErrorToast(ERROR_ADD_OVULATION_GESTATION, response);
                    isSavingSuccess = false;
                }

                return isSavingSuccess;
            } catch (err) {
                throw err;
            }
        },
        handleDamSelect(data) {
            const { isInitial, selectedDam } = data;

            if (isInitial) {
                this.initialValues.selectedDam = selectedDam;
            }

            this.selectedDam = selectedDam;
        },
        handleSireSelect(data) {
            const { selectedSire } = data;
            this.selectedSire = selectedSire;
        },
        handleMatingNoteChange(note) {
            this.matingNotes = note;
        },
        handleChangeFirstDayOfCycle(data) {
            const { isInitial, firstDayOfCycle } = data;

            if (isInitial) {
                this.initialValues.firstDayOfCycle = firstDayOfCycle;
            }

            this.firstDayOfCycle = firstDayOfCycle;
        },
        handleChangeMatingDate(data) {
            const { isInitial, matingDate } = data;

            if (isInitial) {
                this.initialValues.matingDate = matingDate;
            }
            this.matingDate = matingDate;
        },
        handleChangeWhelpingDate(data) {
            const { isInitial, whelpingDate } = data;

            if (isInitial) {
                this.initialValues.whelpingDate = whelpingDate;
            }

            this.whelpingDate = whelpingDate;
        },
        redirectToPrevLocation() {
            if (!this.prevRouteTo) return;

            this.$router.push(this.prevRouteTo);
        },
        async handleTypeOfBirthSelect(data) {
            const { isInitial, whelpingDate, typeOfBirth } = data;
        
            if (isInitial) {
                this.initialValues.whelpingDate = whelpingDate;
                this.initialValues.typeOfBirth = typeOfBirth;
            }
            this.whelpingDate = whelpingDate;
            this.typeOfBirth = typeOfBirth;
            this.isRedirectToLitter = true;
            await this.onSubmitDetails()
        },
    },
    beforeRouteLeave(to, from, next) {
        if(!this.isRedirectToLitter) {
            const isMatingDetailsAdded = this.selectedSire !== null || (this.matingNotes && this.matingNotes.trim() !== "");
            const isDamChanged = (this.initialValues.selectedDam ? this.initialValues.selectedDam.id : null) !== (this.selectedDam ? this.selectedDam.id : null);
            const isFirstDayOfCycleChanged = (this.initialValues.firstDayOfCycle ? this.initialValues.firstDayOfCycle.getTime() : null) !== (this.firstDayOfCycle ? this.firstDayOfCycle.getTime() : null);
            const isMatingDateChanged = (this.initialValues.matingDate ? this.initialValues.matingDate.getTime() : null) !== (this.matingDate ? this.matingDate.getTime() : null);
            const isWhelpingDateChanged = (this.initialValues.whelpingDate ? this.initialValues.whelpingDate.getTime() : null) !== (this.whelpingDate ? this.whelpingDate.getTime() : null);
    
            if (!this.isAddingSuccess && (isFirstDayOfCycleChanged || isMatingDateChanged || isDamChanged || isMatingDetailsAdded || isWhelpingDateChanged)) {
                this.$swal({
                    imageHeight: "30px",
                    title: "You have unsaved changes!",
                    text: "Are you sure you want to leave this page?",
                    imageUrl: `${require("@/assets/images/_shared/dog-confirm.png")}`,
                    confirmButtonColor: "#4B8BFF",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "Confirm",
                    showCancelButton: true,
                    cancelButtonText: "Cancel",
                    reverseButtons: true,
                }).then((result) => {
                    if (result.value) {
                        next(true);
                    } else {
                        next(false);
                    }
                });
            } else {
                next(true);
            }
        } else {
            next(true);
        }
       
    },
};
